import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import {
  FloatingLabelDateTime as DateTime,
  FloatingLabelDateTimePicker as DateTimePicker
} from '../../../Elements';
import { Checkbox, Grid, Message } from 'semantic-ui-react';
import currencyFormatter from 'currency-formatter';
import styles from './PaymentPlan.scss';
import styleVariables from '../../../variables';
import DownPayment from './DownPayment';
import SummaryTotals from './SummaryTotals';
import moment from 'moment';
import PaymentPlanItems from './PaymentPlanItems';

export default class PaymentPlanFields extends React.Component {
  static propTypes = {
    invoice: PropTypes.object.isRequired,
    localInvoice: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    closeHandler: PropTypes.func,
    closeEditor: PropTypes.func,
    getRemainingBalance: PropTypes.func.isRequired,
    getAdditionalPayments: PropTypes.func.isRequired,
    getDownPayment: PropTypes.func.isRequired,
    submitted: PropTypes.bool.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      window: { width: 0, height: 0 }
    };
    this.onPaymentTypeChange = this.onPaymentTypeChange.bind(this);
    this.onIPPlanItemChange = this.onIPPlanItemChange.bind(this);
    this.fieldChange = this.fieldChange.bind(this);
    this.dateFieldChange = this.dateFieldChange.bind(this);
    this.textFieldChange = this.textFieldChange.bind(this);
    this.numberFieldChange = this.numberFieldChange.bind(this);
    this.currencyFieldChange = this.currencyFieldChange.bind(this);
    this.generateId = this.generateId.bind(this);
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);

    const paymentType = (() => {
      if (this.props.invoice.down_payment_distribution_type) {
        return 'multiple';
      }
      return this.props.invoice.i_pplan_items.edges.length === 1
        ? 'full'
        : null;
    })();

    this.state = {
      paymentType
    };
  }

  componentWillMount() {
    this.updateWindowDimensions();
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    if (this.props.closeEditor) this.props.closeEditor();
  }

  onPaymentTypeChange(e, data) {
    const invoice = _.cloneDeep(this.props.invoice);
    if (data.value === 'full') {
      this.setState({ paymentType: 'full' });
      invoice.down_payment_cents = null;
      invoice.down_payment_currency = null;
      invoice.down_payment_distribution_type = null;
      invoice.down_payment_due_at = null;
      invoice.down_payment_percent = null;
      invoice.pplan_distribution_type = 'FIXED';
      invoice.delete_pplan_items_with_ids = invoice.i_pplan_items.edges.map(
        item => item.node.id
      );
      invoice.i_pplan_items = {
        edges: [
          {
            node: {
              amount_cents: this.props.invoice.total_cents,
              amount_currency: 'USD',
              due_at: null,
              locked: false,
              percent: null,
              tempId: this.generateId()
            }
          }
        ]
      };
    } else {
      this.setState({ paymentType: 'multiple' });
      invoice.down_payment_cents = null;
      invoice.down_payment_currency = 'USD';
      invoice.down_payment_distribution_type = 'FIXED';
      invoice.down_payment_due_at = null;
      invoice.down_payment_percent = null;
      invoice.pplan_distribution_type = 'FIXED';
      invoice.i_pplan_items =
        invoice.i_pplan_items.edges.length === 0
          ? {
            edges: [
              {
                node: {
                  amount_cents: null,
                  amount_currency: 'USD',
                  due_at: null,
                  locked: false,
                  percent: null,
                  tempId: this.generateId()
                }
              }
            ]
          }
          : invoice.i_pplan_items;
    }
    this.props.onChange(invoice);
  }

  onIPPlanItemChange(e, idx) {
    const localInvoice = _.cloneDeep(this.props.localInvoice);
    localInvoice.i_pplan_items.edges[idx].node[e.target.name] = e.target.value;
    this.props.onChange(localInvoice);
  }

  generateId() {
    return `${Math.random().toString(36)}00000000000000000`.slice(2, 16 + 2);
  }

  fieldChange(key, value) {
    const newValue = {};
    newValue[key] = value;
    this.props.onChange(Object.assign(this.props.localInvoice, newValue));
  }

  dateFieldChange(e) {
    this.fieldChange(e.target.name, moment(e.target.value).toISOString());
  }

  textFieldChange(e) {
    this.fieldChange(e.target.name, e.target.value);
  }

  numberFieldChange(e) {
    this.fieldChange(e.target.name, parseInt(e.target.value, 10));
  }

  currencyFieldChange(e) {
    this.fieldChange(e.target.name, e.target.value * 100);
  }

  updateWindowDimensions() {
    this.setState({
      window: { width: window.innerWidth, height: window.innerHeight }
    });
  }

  render() {
    const { localInvoice } = this.props;
    const DateInput =
      this.state.window.width < styleVariables.tabletBreakpoint
        ? DateTime
        : DateTimePicker;

    return (
      <Grid padded relaxed stackable>
        <Grid.Row columns={1}>
          <Grid.Column style={{ paddingLeft: '2em', paddingRight: '2em' }}>
            <h3>How would you like to collect payment?</h3>
            <br />
            <div>
              <Checkbox
                radio
                checked={this.state.paymentType === 'full'}
                onChange={this.onPaymentTypeChange}
                value='full'
                label='Payment In Full'
                className={styles.checkbox}
              />
              <br />
              {this.state.paymentType === 'full' &&
                <div>
                  <div
                    style={{
                      display: 'inline-block',
                      marginTop: '1.75em'
                    }}
                  >
                    A full payment of{' '}
                    <strong>
                      {currencyFormatter.format(
                        (this.props.invoice.total_cents || 0) / 100,
                        {
                          code: 'USD'
                        }
                      )}
                    </strong>&nbsp;
                  </div>
                  <div
                    style={{
                      display: 'inline-block',
                      marginTop: '1.75em',
                      marginRight: '0.5em'
                    }}
                  >
                    is due on
                  </div>
                  <DateInput
                    dateOnly
                    value={
                      (localInvoice.i_pplan_items.edges.length > 0 &&
                        localInvoice.i_pplan_items.edges[0].node.due_at) ||
                      ''
                    }
                    onChange={e => this.onIPPlanItemChange(e, 0)}
                    name='due_at'
                    datePlaceholder='Date'
                    placeholder='Date'
                    label=''
                    required
                    validationError='Required'
                    style={{
                      marginTop: '1.25em'
                    }}
                    inline
                    viewer={null}
                    submitted={this.props.submitted}
                  />
                </div>}
            </div>
            <div>
              <Checkbox
                radio
                checked={this.state.paymentType === 'multiple'}
                onChange={this.onPaymentTypeChange}
                value='multiple'
                label='Multiple Payments'
                className={styles.checkbox}
              />
            </div>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={1}>
          <Grid.Column style={{ paddingLeft: '2em', paddingRight: '2em' }}>
            {this.state.paymentType === 'multiple' &&
              <div>
                <h3>Down Payment</h3>
                <DownPayment
                  localInvoice={localInvoice}
                  dateFieldChange={this.dateFieldChange}
                  submitted={this.props.submitted}
                  currencyFieldChange={this.currencyFieldChange}
                  numberFieldChange={this.numberFieldChange}
                  textFieldChange={this.textFieldChange}
                  DateInput={DateInput}
                />
                <h3>Additional Payments</h3>
                <PaymentPlanItems
                  localInvoice={localInvoice}
                  onChange={this.props.onChange}
                  onIPPlanItemChange={this.onIPPlanItemChange}
                  DateInput={DateInput}
                  submitted={this.props.submitted}
                />
                {this.props.submitted &&
                  this.props.getRemainingBalance() !== 0 &&
                  <Message negative>
                    <Message.Header>
                      Remaining balance must be zero
                    </Message.Header>
                    <p>
                      The down payment and additional payments must add up to
                      the invoice total
                    </p>
                  </Message>}
                <SummaryTotals
                  localInvoice={this.props.localInvoice}
                  getDownPayment={this.props.getDownPayment}
                  getAdditionalPayments={this.props.getAdditionalPayments}
                  getRemainingBalance={this.props.getRemainingBalance}
                />
              </div>}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Document/Sections/PaymentPlan/PaymentPlanFields.js